import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import * as ExcelJS from 'exceljs';
import { CommonService } from 'src/app/lib/services/common.service';
import { CatalogueService } from 'src/app/lib/services/catalouge.service';
import { Constants } from 'src/app/config/constants';
import { StorageService } from 'src/app/lib/services/storage.service';

@Component({
    selector: 'app-bulk-update',
    templateUrl: './bulk-update.component.html',
    styleUrls: ['./bulk-update.component.scss'],
    standalone: false
})
export class BulkUpdateComponent implements OnInit {
  fileName: string = 'products_data.xlsx';
  dataSource: any[] = [];
  jsonData: any[] = [];
  file: File | null = null;
  shopID!: string | null;
  selectedFile: File | null = null;
  selectedFileName: string;

  constructor(
    private router: Router,
    private catalogueService: CatalogueService,
    public commonService: CommonService,
    private storageservice: StorageService,
  ) { }

  ngOnInit(): void { }

  async exportToExcel() {
    const isLoading: any = await this.commonService.presentLoader();
    isLoading.present();
    const shopId = this.storageservice.getItem(Constants.SHOP_ID);
    this.catalogueService.getDataByShop(shopId).subscribe({
      next: async (res: any) => {
        this.dataSource = res.data;
        isLoading.dismiss();
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sheet1');
        this.defineWorksheetColumns(worksheet);
        this.styleHeaderRow(worksheet);
        // Await the image promises
        await this.addProductRows(worksheet, workbook);
        this.protectSpecificFields(worksheet);
        this.saveWorkbook(workbook);
      },
      error: (error) => {
        isLoading.dismiss();
        console.error(error);
        this.commonService.errorToast(error);
      }
    });
  }

  // Function to define worksheet columns
  defineWorksheetColumns(worksheet: any) {
    worksheet.columns = [
      { header: 'ProductId', key: 'ProductId', width: 30 },
      // Add product image twice: once for removing the URL in the sheet and once for restoring it to pass data to the API.
      { header: 'ImageDisplay', width: 20 },
      { header: 'Product Image', key: 'ProductImage', width: 20 },
      { header: 'ProductName', key: 'ProductName', width: 30 },
      { header: 'Category', key: 'Category', width: 20 },
      { header: 'SubCategory', key: 'SubCategory', width: 20 },
      { header: 'SellingPrice', key: 'SellingPrice', width: 15 },
      { header: 'ProductStatus', key: 'ProductStatus', width: 20 },
      { header: 'PreOrderDiscount', key: 'PreOrderDiscount', width: 20 },
      { header: 'SoldBy', key: 'SoldBy', width: 20 },
      { header: 'MinimumQty', key: 'MinimumQty', width: 20 },
      { header: 'Grading', key: 'Grading', width: 20 },
    ];
  }

  // Function to style the header row
  styleHeaderRow(worksheet: any) {
    const headerRow = worksheet.getRow(1);
    headerRow.font = { size: 12, bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'C6EEED' },
    };
    headerRow.height = 25;

    headerRow.eachCell({ includeEmpty: true }, (cell: any) => {
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } }
      };
    });
  }

  // Function to load images from URLs
  async loadImage(url: string): Promise<string> {
    const response = await fetch(url);
    const blob = await response.blob();
    const reader = new FileReader();

    return new Promise<string>((resolve, reject) => {
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  async addProductRows(worksheet: any, workbook: any) {
    const imagePromises = this.dataSource.map(async (product: any) => {
      const imageBase64 = await this.loadImage(product.ProductImage);

      // Add the row including ProductImage
      const row = worksheet.addRow({
        ProductId: product.ProductId,
        ProductImage: '',
        ImageDisplay: product.ProductImage,
        ProductName: product.ProductName,
        Category: product.Category,
        SubCategory: product.SubCategory,
        SellingPrice: product.SellingPrice,
        ProductStatus: product.ProductStatus,
        PreOrderDiscount: product.PreOrderDiscount,
        SoldBy: product.SoldBy,
        MinimumQty: product.MinimumQty,
        Grading: product.Grading,
      });

      worksheet.getRow(row.number).height = 30;

      const imageId = workbook.addImage({
        base64: imageBase64,
        extension: 'png',
      });

      worksheet.addImage(imageId, {
        tl: { col: 1, row: row.number - 1 },
        ext: { width: 40, height: 40 },
      });
      // Add validations
      this.addValidations(worksheet, row.number);

    });
    await Promise.all(imagePromises);
    const productImageColumnIndex = 2;
    worksheet.getColumn(productImageColumnIndex).hidden = true;
  }

  addValidations(worksheet: any, rowIndex: number) {
    // Validation for SellingPrice (must be >= 0)
    worksheet.getCell(`G${rowIndex}`).dataValidation = {
      type: 'whole',
      operator: 'greaterThanOrEqual',
      formula1: 0,
      showErrorMessage: true,
      errorTitle: 'Invalid Selling Price',
      error: 'Selling Price must Number.',
    };

    // Validation for MinimumQty (must be >= 0)
    worksheet.getCell(`K${rowIndex}`).dataValidation = {
      type: 'whole',
      operator: 'greaterThanOrEqual',
      formula1: 0,
      showErrorMessage: true,
      errorTitle: 'Invalid Minimum Quantity',
      error: 'Minimum Quantity must be Number.',
    };

    // Validation for PreOrderDiscount 
    worksheet.getCell(`I${rowIndex}`).dataValidation = {
      type: 'whole',
      operator: 'greaterThanOrEqual',
      formula1: 0,
      showErrorMessage: true,
      errorTitle: 'Invalid PreOrder Discount',
      error: 'PreOrder Discount must Number.',
    };

    // Validation for Grading (must be an integer between 1 and 5)
    worksheet.getCell(`L${rowIndex}`).dataValidation = {
      type: 'whole',
      operator: 'greaterThanOrEqual',
      formula1: 0,
      showErrorMessage: true,
      errorTitle: 'Invalid Grading',
      error: 'Grading must Number',
    };
  }

  // Function to protect only specific fields in the worksheet
  protectSpecificFields(worksheet: any) {
    worksheet.protect('', {});
    worksheet.eachRow({ includeEmpty: false }, (row: any, rowIndex: number) => {
      if (rowIndex > 1) {
        worksheet.getCell(`G${rowIndex}`).protection = { locked: false }; // SellingPrice
        worksheet.getCell(`H${rowIndex}`).protection = { locked: false }; // ProductStatus
        worksheet.getCell(`I${rowIndex}`).protection = { locked: false }; // PreOrderDiscount
        worksheet.getCell(`J${rowIndex}`).protection = { locked: false }; // SoldBy
        worksheet.getCell(`K${rowIndex}`).protection = { locked: false }; // MinimumQty
        worksheet.getCell(`L${rowIndex}`).protection = { locked: false }; // Grading
      }
    });
  }

  // Function to save the workbook as an Excel file
  saveWorkbook(workbook: any) {
    workbook.xlsx.writeBuffer().then((buffer: any) => {
      const blob = new Blob([buffer], { type: 'application/octet-stream' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = this.fileName;
      link.click();
    });
  }

  onFileChange(event: any) {
    const target = event.target as HTMLInputElement;

    if (target.files && target.files.length > 0) {
      this.file = target.files[0]; // Store the selected file
      this.selectedFileName = target.files[0].name;
    } else {
      this.file = null;
    }
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      this.selectedFile = file;
    }
  }

  uploadFile() {
    if (!this.file) {
      alert('Please select a file to upload');
      return;
    }

    const formData = new FormData();
    formData.append('file', this.file);  // 'file' is the key expected by the API
    const shopId = this.storageservice.getItem(Constants.SHOP_ID);

    this.catalogueService.bulkUpdate(shopId, formData).subscribe({
      next: (res: any) => {
        const updateStatuses = res.data.map((item: any) => item);
        this.storageservice.set('updateDataStatus', updateStatuses);
        this.router.navigate(['/updated-productlist', shopId]);
        // Clear the file after successful upload
        this.file = null;
        this.selectedFileName = '';
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = ''; // Reset the input field
        }
      },
      error: (error) => {
        console.error('Error uploading file', error);
      }
    });
  }

  gotoMyCatalog() {
    this.router.navigate(['/my-catalog']);
  }
}
